import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import MatTableToolbar from './components/MatTableToolbar';
import Course from '@/models/course';
import { useCourses } from '@/services/courses';
import CourseModal from './components/course-modal';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { formatDate } from '@/utils/helpers';
import { useCategories } from '@/services/categories';

// Custom accessor — some sort columns (lessonCount, isFlagship) are computed
// off Course rather than being direct properties. Falling back to a[orderBy]
// would silently sort by `undefined` and look broken in the UI.
const valueFor = (row: Course, key: string): string | number => {
    switch (key) {
        case 'lessonCount':
            return row.lessons?.length ?? 0;
        case 'isFlagship':
            return row.isFlagship ? 1 : 0;
        case 'orderIndex':
            return row.orderIndex ?? Number.MAX_SAFE_INTEGER;
        default: {
            const v = (row as unknown as Record<string, unknown>)[key];
            return (v as string | number) ?? '';
        }
    }
};

const getSorting = (order: 'asc' | 'desc', orderBy: string) => {
    return (a: Course, b: Course) => {
        const av = valueFor(a, orderBy);
        const bv = valueFor(b, orderBy);
        if (av < bv) return order === 'desc' ? 1 : -1;
        if (av > bv) return order === 'desc' ? -1 : 1;
        return 0;
    };
};

const Courses = () => {
    // Course-scoped categories — courses have their own category pool (not the
    // App-content one). Manage them under Categories (type = Course).
    const { data: categories } = useCategories('course');
    const { data: courses, isLoading } = useCourses();
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState('updated_at');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filteredCourses, setFilteredCourses] = useState<Course[]>(courses?.data || []);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState<string>();

    useEffect(() => {
        if (courses?.data) {
            setFilteredCourses(courses.data);
        }
    }, [courses]);

    const toggle = () => setIsOpen((prev) => !prev);

    const handleRequestAdd = () => {
        setSelectedCourseId(undefined);
        toggle();
    };

    const handleRequestSort = (_: React.MouseEvent<unknown>, property: string) => {
        let orderTemp: 'asc' | 'desc' = 'desc';
        if (orderBy === property && order === 'desc') {
            orderTemp = 'asc';
        }
        setOrder(orderTemp);
        setOrderBy(property);
    };

    const handleClick = (id: string) => {
        setSelectedCourseId(id);
        toggle();
    };

    const handleChangePage = (_: unknown, item: number) => setPage(item);
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Courses</h3>
                    <h3 className="page-subhead subhead">
                        Multi-lesson audio/video courses. Premium app users can play them; site lists them as marketing material.
                    </h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <MaterialTextField
                                            className="w-50"
                                            label="Search by Title or Slug"
                                            type="text"
                                            onChange={(e) => {
                                                const q = e.target.value.toLowerCase();
                                                setFilteredCourses(
                                                    courses?.data?.filter(
                                                        (c) => c.title.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q),
                                                    ) || [],
                                                );
                                            }}
                                        />
                                        <MatTableToolbar onRequestAdd={handleRequestAdd} />
                                    </div>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                            <TableBody>
                                                {filteredCourses
                                                    .slice()
                                                    .sort(getSorting(order, orderBy))
                                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    .map((d) => (
                                                        <TableRow role="checkbox" onClick={() => handleClick(d.id)} tabIndex={-1} key={d.id}>
                                                            <TableCell component="th" scope="row" padding="none">
                                                                {d.slug}
                                                            </TableCell>
                                                            <TableCell>{d.title}</TableCell>
                                                            <TableCell>{d.trackType}</TableCell>
                                                            <TableCell>{d.subscriptionPlan}</TableCell>
                                                            <TableCell>{d.lessons?.length ?? 0}</TableCell>
                                                            <TableCell>{d.isFlagship ? 'Yes' : ''}</TableCell>
                                                            <TableCell>{d.orderIndex ?? ''}</TableCell>
                                                            <TableCell>{formatDate(d.updated_at)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                {filteredCourses.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={8} className="text-center">
                                                            No Courses yet. Add one to get started.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={filteredCourses.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        dir="ltr"
                                        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <CourseModal isOpen={isOpen} toggle={toggle} selectedCourseId={selectedCourseId} categories={categories?.data || []} />
        </Container>
    );
};

export default Courses;
